import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import ModalShell from './ModalShell';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import IndeterminateProgress from '../../../../components/IndeterminateProgress';

// Shift work log — collected on the way OUT and submitted as `work_logs` rows
// on the punch itself. There is no separate endpoint and no way to edit a row
// once the punch is recorded. Shown when the society has
// attendance_work_log_enabled = 1 (the BE then rejects an OUT punch carrying no
// rows, 400 WORK_LOG_REQUIRED), or when the employee has daily to-do items not
// yet answered today. Each to-do must get a status (sent as a row with
// todo_id; 400 TODO_REQUIRED otherwise); free-form rows are then optional.

const MAX_ENTRIES = 50;
const MAX_WORK_TYPE = 100;
const MAX_DESCRIPTION = 2000;

// Fallback for the status dropdown until /attendance/enums answers.
const FALLBACK_STATUSES = ['COMPLETED', 'IN_PROGRESS', 'PENDING'];

// work_type is free text server-side (VARCHAR(100), deliberately not an enum)
// so a society can name its own trades. These are suggestions only — the input
// stays typeable.
const WORK_TYPE_SUGGESTIONS = [
    'Housekeeping',
    'Security',
    'Gardening',
    'Plumbing',
    'Electrical',
    'Carpentry',
    'Painting',
    'Pest control',
    'Lift maintenance',
    'Water tank / pump',
    'Garbage collection',
    'Repairs',
    'Inspection',
    'Administrative',
];

const STATUS_META = {
    COMPLETED: { label: 'Completed', dot: 'tw-bg-emerald-500' },
    IN_PROGRESS: { label: 'In progress', dot: 'tw-bg-blue-500' },
    PENDING: { label: 'Pending', dot: 'tw-bg-amber-500' },
};

const statusMeta = (s) => STATUS_META[s] || {
    label: String(s || '').replace(/_/g, ' '),
    dot: 'tw-bg-gray-400',
};

let rowSeq = 0;
const blankRow = () => ({ key: `wl-${++rowSeq}`, work_type: '', description: '', status: 'COMPLETED' });

// The status a to-do answer can take, as buttons.
const TODO_CHOICES = [
    { value: 'COMPLETED', label: 'Done', active: 'tw-bg-emerald-600 tw-border-emerald-600 tw-text-white' },
    { value: 'IN_PROGRESS', label: 'In progress', active: 'tw-bg-blue-600 tw-border-blue-600 tw-text-white' },
    { value: 'PENDING', label: 'Not done', active: 'tw-bg-amber-500 tw-border-amber-500 tw-text-white' },
];

const WorkLogModal = ({ isOpen, onClose, onSubmit, busy = false, initialRows = [], statuses, todos = [] }) => {
    const [rows, setRows] = useState([blankRow()]);
    const [errors, setErrors] = useState({});
    // todo_id -> { status, remark } for today's unanswered to-do items.
    const [answers, setAnswers] = useState({});
    const hasTodos = todos.length > 0;

    // Reseed each time the modal opens: a punch rejected for some other reason
    // (geofence, selfie) hands the typed rows back so nothing is retyped.
    useEffect(() => {
        if (!isOpen) return;
        const freeRows = initialRows.filter(r => r.todo_id == null);
        setRows(freeRows.length > 0
            ? freeRows.map(r => ({ ...blankRow(), ...r }))
            : [blankRow()]);
        setAnswers(Object.fromEntries(initialRows
            .filter(r => r.todo_id != null)
            .map(r => [r.todo_id, { status: r.status || '', remark: r.description || '' }])));
        setErrors({});
        // initialRows is a fresh array on every parent render — keying off
        // isOpen alone is what makes this "on open" and not "on every render".
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const statusOptions = ((Array.isArray(statuses) && statuses.length > 0) ? statuses : FALLBACK_STATUSES)
        .map(s => ({ value: s, label: statusMeta(s).label }));

    const setField = (key, field, value) => {
        setRows(prev => prev.map(r => (r.key === key ? { ...r, [field]: value } : r)));
        setErrors(prev => ({ ...prev, [`${key}.${field}`]: undefined }));
    };

    const addRow = () => {
        if (rows.length >= MAX_ENTRIES) {
            toast.warn(`A punch can carry at most ${MAX_ENTRIES} work log entries`);
            return;
        }
        setRows(prev => [...prev, blankRow()]);
    };

    const removeRow = (key) => {
        setRows(prev => (prev.length === 1 ? prev : prev.filter(r => r.key !== key)));
        setErrors(prev => ({ ...prev, [`${key}.work_type`]: undefined, [`${key}.description`]: undefined }));
    };

    const setAnswer = (todoId, field, value) => {
        setAnswers(prev => ({ ...prev, [todoId]: { status: '', remark: '', ...prev[todoId], [field]: value } }));
        setErrors(prev => ({ ...prev, [`todo.${todoId}`]: undefined }));
    };

    const handleSubmit = () => {
        const nextErrors = {};
        todos.forEach(t => {
            if (!answers[t.todo_id]?.status) nextErrors[`todo.${t.todo_id}`] = 'Pick a status';
        });
        // With to-dos on the list, free-form rows are optional: blank ones are dropped.
        const freeRows = hasTodos ? rows.filter(r => r.work_type.trim() || r.description.trim()) : rows;
        freeRows.forEach(r => {
            const workType = r.work_type.trim();
            const description = r.description.trim();
            if (!workType) nextErrors[`${r.key}.work_type`] = 'Work type is required';
            else if (workType.length > MAX_WORK_TYPE) nextErrors[`${r.key}.work_type`] = `Max ${MAX_WORK_TYPE} characters`;
            if (!description) nextErrors[`${r.key}.description`] = 'Description is required';
            else if (description.length > MAX_DESCRIPTION) nextErrors[`${r.key}.description`] = `Max ${MAX_DESCRIPTION} characters`;
        });

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            toast.warn(Object.keys(nextErrors).some(k => k.startsWith('todo.'))
                ? 'Mark every to-do item before punching out'
                : 'Fill in the work type and description for every entry');
            return;
        }

        // Trimmed on the way out only, so the draft kept for a retry stays
        // exactly what was typed.
        onSubmit([
            ...todos.map(t => ({
                todo_id: t.todo_id,
                status: answers[t.todo_id].status,
                description: (answers[t.todo_id].remark || '').trim(),
            })),
            ...freeRows.map(r => ({
                work_type: r.work_type.trim(),
                description: r.description.trim(),
                status: r.status || 'COMPLETED',
            })),
        ]);
    };

    // Borderless cell inputs — the row card carries the chrome, so a filled
    // table reads as rows of text rather than a wall of boxes.
    const cellClass = (err) =>
        `tw-w-full tw-bg-transparent tw-border-0 tw-rounded-md tw-px-2 tw-py-1.5 tw-text-[13px] tw-text-gray-800 placeholder:tw-text-gray-300 focus:tw-outline-none focus:tw-bg-white focus:tw-ring-1 ${err ? 'tw-ring-1 tw-ring-red-300 tw-bg-red-50/60' : 'focus:tw-ring-blue-400'}`;

    const invalidCount = Object.values(errors).filter(Boolean).length;

    return (
        <ModalShell
            isOpen={isOpen}
            title="Work log for this shift"
            icon="mdi:clipboard-text-clock-outline"
            onClose={onClose}
            busy={busy}
            maxWidth="tw-max-w-5xl"
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={busy}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-rose-500 tw-text-white tw-text-xs tw-font-semibold tw-shadow-sm hover:tw-bg-rose-600 disabled:tw-opacity-50"
                >
                    {busy ? <Icon icon="mdi:loading" className="tw-animate-spin tw-text-sm" /> : <Icon icon="mdi:logout" className="tw-text-sm" />}
                    {busy ? 'Punching out…' : 'Submit & punch out'}
                </button>
            }
        >
            {/* Submit in flight — the whole form is locked, so show where it is */}
            <IndeterminateProgress
                active={busy}
                color="tw-bg-rose-500"
                label="Submitting your work log and punching out…"
                className="tw-mb-3"
            />

            {/* Today's to-do list - every item needs a status */}
            {hasTodos && (
                <div className="tw-mb-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0 tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:format-list-checks" className="tw-text-rose-500" /> Today's to-do list
                        </h3>
                        <span className="tw-text-[11px] tw-text-gray-400">
                            {todos.filter(t => answers[t.todo_id]?.status).length} / {todos.length} marked
                        </span>
                    </div>
                    <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-divide-y tw-divide-gray-100">
                        {todos.map(t => {
                            const a = answers[t.todo_id] || {};
                            const err = errors[`todo.${t.todo_id}`];
                            return (
                                <div key={t.todo_id} className={`tw-px-3 tw-py-2.5 ${err ? 'tw-bg-red-50/40' : ''}`}>
                                    <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[13px] tw-font-medium tw-text-gray-800 tw-m-0">{t.title}</p>
                                            {t.description && <p className="tw-text-[11px] tw-text-gray-500 tw-m-0">{t.description}</p>}
                                        </div>
                                        <div className="tw-flex tw-gap-1">
                                            {TODO_CHOICES.map(c => (
                                                <button
                                                    key={c.value}
                                                    type="button"
                                                    onClick={() => setAnswer(t.todo_id, 'status', c.value)}
                                                    disabled={busy}
                                                    className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-border ${a.status === c.value ? c.active : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                                                >
                                                    {c.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <input
                                        type="text"
                                        value={a.remark || ''}
                                        onChange={(e) => setAnswer(t.todo_id, 'remark', e.target.value)}
                                        disabled={busy}
                                        maxLength={MAX_DESCRIPTION}
                                        placeholder="Remark (optional)"
                                        className={`${cellClass(false)} tw-mt-1.5 tw-border tw-border-gray-100`}
                                    />
                                    {err && <p className="tw-text-[11px] tw-text-red-500 tw-m-0 tw-mt-1">{err}</p>}
                                </div>
                            );
                        })}
                    </div>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-3 tw-mb-0">Other work done this shift <span className="tw-text-gray-400">(optional)</span></p>
                </div>
            )}

            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-mb-3">
                <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-max-w-2xl">
                    One row per piece of work. Anything left <span className="tw-font-medium tw-text-blue-700">In progress</span> or{' '}
                    <span className="tw-font-medium tw-text-amber-700">Pending</span> is what your supervisor picks up next shift.
                </p>
                <span className="tw-text-[11px] tw-text-gray-400 tw-whitespace-nowrap tw-mt-0.5">
                    {rows.length} / {MAX_ENTRIES}
                </span>
            </div>

            {/* Entry table — header + one row per entry */}
            <div className="tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden">
                <div className="tw-hidden md:tw-grid tw-grid-cols-12 tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-200">
                    <div className="tw-col-span-3 tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Work type</div>
                    <div className="tw-col-span-6 tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">What was done</div>
                    <div className="tw-col-span-2 tw-text-[10px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Status</div>
                    <div className="tw-col-span-1" />
                </div>

                <div className="tw-divide-y tw-divide-gray-100">
                    {rows.map((row, idx) => {
                        const meta = statusMeta(row.status);
                        const rowInvalid = errors[`${row.key}.work_type`] || errors[`${row.key}.description`];
                        return (
                            <div
                                key={row.key}
                                className={`tw-grid tw-grid-cols-1 md:tw-grid-cols-12 tw-gap-2 tw-items-center tw-px-3 tw-py-2 tw-transition-colors ${rowInvalid ? 'tw-bg-red-50/40' : 'hover:tw-bg-gray-50/60'}`}
                            >
                                <div className="md:tw-col-span-3">
                                    <span className="md:tw-hidden tw-block tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-0.5">
                                        Entry {idx + 1} · Work type
                                    </span>
                                    <input
                                        type="text"
                                        list="work-log-type-suggestions"
                                        value={row.work_type}
                                        onChange={(e) => setField(row.key, 'work_type', e.target.value)}
                                        disabled={busy}
                                        maxLength={MAX_WORK_TYPE}
                                        placeholder="Housekeeping"
                                        title={errors[`${row.key}.work_type`] || ''}
                                        className={cellClass(errors[`${row.key}.work_type`])}
                                    />
                                </div>

                                <div className="md:tw-col-span-6">
                                    <span className="md:tw-hidden tw-block tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-0.5">
                                        What was done
                                    </span>
                                    <input
                                        type="text"
                                        value={row.description}
                                        onChange={(e) => setField(row.key, 'description', e.target.value)}
                                        disabled={busy}
                                        maxLength={MAX_DESCRIPTION}
                                        placeholder="Swept and mopped towers A and B"
                                        title={errors[`${row.key}.description`] || row.description}
                                        className={cellClass(errors[`${row.key}.description`])}
                                    />
                                </div>

                                <div className="md:tw-col-span-2">
                                    <span className="md:tw-hidden tw-block tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-0.5">
                                        Status
                                    </span>
                                    <div className="tw-flex tw-items-center tw-gap-1.5">
                                        <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full tw-flex-shrink-0 ${meta.dot}`} title={meta.label} />
                                        {/* Options portal at z-9999, above ModalShell's z-9000 */}
                                        <SearchableDropdown
                                            value={row.status}
                                            onChange={(v) => setField(row.key, 'status', v)}
                                            options={statusOptions}
                                            clearable={false}
                                            disabled={busy}
                                            small
                                            className="tw-flex-1 tw-min-w-0"
                                        />
                                    </div>
                                </div>

                                <div className="md:tw-col-span-1 tw-flex md:tw-justify-end">
                                    <button
                                        type="button"
                                        onClick={() => removeRow(row.key)}
                                        disabled={busy || rows.length === 1}
                                        className="tw-flex tw-items-center tw-gap-1 tw-w-7 tw-h-7 md:tw-justify-center tw-rounded-md tw-text-gray-300 hover:tw-text-red-500 hover:tw-bg-red-50 disabled:tw-opacity-0 disabled:tw-cursor-default tw-transition-colors"
                                        title="Remove this entry"
                                    >
                                        <Icon icon="mdi:close" className="tw-text-base" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={addRow}
                    disabled={busy || rows.length >= MAX_ENTRIES}
                    className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2.5 tw-text-xs tw-font-medium tw-text-blue-600 tw-bg-gray-50/70 tw-border-t tw-border-gray-100 hover:tw-bg-blue-50 hover:tw-text-blue-700 disabled:tw-opacity-40 tw-transition-colors"
                >
                    <Icon icon="mdi:plus" className="tw-text-sm" /> Add another entry
                </button>
            </div>

            <datalist id="work-log-type-suggestions">
                {WORK_TYPE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
            </datalist>

            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mt-2.5">
                <p className="tw-text-[11px] tw-text-gray-400 tw-m-0">
                    Saved with your punch — entries can't be edited afterwards.
                </p>
                {invalidCount > 0 && (
                    <p className="tw-text-[11px] tw-text-red-500 tw-m-0 tw-flex tw-items-center tw-gap-1">
                        <Icon icon="mdi:alert-circle-outline" />
                        Complete the highlighted fields
                    </p>
                )}
            </div>
        </ModalShell>
    );
};

export default WorkLogModal;
